import * as React from 'react';
import TailwindModal from './Tailwind/TailwindModal';
import AuditabilityGroup from './Auditability/AuditabilityGroup';
import { generateString } from '../pages/subpages/Dashboard';
import { useAuditData } from '../shared/hooks/queries/useAudit';

// Count all tests recursively
const countAllTests = (groups: any[]): { passed: number; failed: number } => {
    let passed = 0;
    let failed = 0;

    const countGroup = (group: any) => {
        group.checks.forEach((test: any) => {
            if (test.is_valid) passed++;
            else failed++;
        });
        group.subgroups.forEach(countGroup);
    };

    groups.forEach(countGroup);
    return { passed, failed };
};

export default function AuditabilityModal({ open, setOpen, scenario, sensitivity }) {
    const entityId = sensitivity === 'null' ? scenario?.id : sensitivity?.id;
    const entityType = sensitivity === 'null' ? 'scenario' : 'sensitivity';

    const { data: auditData = [], isLoading: loading } = useAuditData(
        open ? (entityId ?? 0) : 0,
        entityType,
    );

    // Calculate summary stats
    const { passed, failed } = React.useMemo(() => countAllTests(auditData), [auditData]);
    const total = passed + failed;
    const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;
    const allPassed = failed === 0 && total > 0;

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <TailwindModal
            isOpen={open}
            onClose={handleClose}
            title={`${(sensitivity === 'null') ? scenario?.name : sensitivity?.name} Audit`}
            size="full"
            showCloseButton={true}
        >
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="text-slate-500">Loading audit data...</p>
                    </div>
                </div>
            ) : auditData.length > 0 ? (
                <div className="space-y-6">
                    {/* Summary Card */}
                    <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                        <div className="flex flex-wrap items-center gap-6">
                            {/* Status indicator */}
                            <div className="flex items-center gap-3">
                                <div className={`size-10 rounded-full flex items-center justify-center ${allPassed ? 'bg-emerald-100' : 'bg-red-100'}`}>
                                    <span className={`material-symbols-outlined text-[24px] ${allPassed ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {allPassed ? 'check_circle' : 'error'}
                                    </span>
                                </div>
                                <div>
                                    <p className={`font-bold ${allPassed ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {allPassed ? 'All Tests Passed' : 'Issues Found'}
                                    </p>
                                    <p className="text-xs text-slate-500">{passRate}% pass rate</p>
                                </div>
                            </div>

                            <div className="h-8 w-px bg-slate-200 hidden sm:block" />

                            {/* Stats */}
                            <div className="flex gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium">
                                        <span className="material-symbols-outlined text-[16px]">check</span>
                                        {passed} passed
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-sm font-medium">
                                        <span className="material-symbols-outlined text-[16px]">close</span>
                                        {failed} failed
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Info text */}
                    <p className="text-sm text-slate-600">
                        These tests are key indicators for the consistency of the model.
                        <span className="text-slate-400 ml-1">Tests are carried out with a certain degree of tolerance.</span>
                    </p>

                    {/* Test Groups */}
                    <div>
                        {auditData.map(auditGroup => (
                            <AuditabilityGroup
                                key={generateString(10)}
                                group={auditGroup}
                                identifier={generateString(10)}
                            />
                        ))}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="size-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                        <span className="material-symbols-outlined text-[28px] text-slate-400">fact_check</span>
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No Audit Data</h3>
                    <p className="text-slate-500 max-w-md">
                        A company is required to access the audit.
                    </p>
                </div>
            )}
        </TailwindModal>
    );
}
